import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table'

import { PaymentCollectionDetailedApiResponse } from './PaymentCollectionSearchForm'

const columnHelper = createColumnHelper<PaymentCollectionDetailedApiResponse>()

export default function PaymentAllocationDetailedList({
  data
}: {
  data: PaymentCollectionDetailedApiResponse[]
  //   setShowDetailedView: React.Dispatch<React.SetStateAction<boolean>>
  //   onViewDetails: (allocationId: string, companyName: string) => void // New prop
}) {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })

  const columns = [
    columnHelper.accessor('payment_allocation_id', {
      header: 'Order ID',
      cell: (info) => (
        <Typography variant='body2' fontWeight='medium'>
          {info.getValue()}
        </Typography>
      )
    }),
    // columnHelper.accessor('company_name', {
    //   header: 'Currency', // Fixed typo
    //   cell: (info) => (
    //     <Typography variant='body2' fontWeight={600}>
    //       {info.getValue()}
    //     </Typography>
    //   )
    // }),
    columnHelper.accessor('received_amount', {
      header: 'Package Name',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('received_amount', {
      header: 'Order Amount',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('mode_of_payment', {
      header: 'Payment Mode',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('already_received', {
      header: 'Already Paid',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('last_pending_amount', {
      header: 'Pending Amount',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('received_amount', {
      header: 'Amount Received (In this Allocation)',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    })
  ]

  const table = useReactTable({
    data,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  })

  const { pageIndex, pageSize } = table.getState().pagination

  // Keep page in range if data changes
  const totalRows = table.getPrePaginationRowModel().rows.length
  useEffect(() => {
    const maxPage = Math.max(Math.ceil(totalRows / pageSize) - 1, 0)
    if (pageIndex > maxPage) table.setPageIndex(maxPage)
  }, [pageIndex, pageSize, totalRows, table])

  const paginatedRowCount = table.getFilteredRowModel
    ? table.getFilteredRowModel().rows.length
    : totalRows

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: 'action.hover' }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table?.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>No Statement found</Typography>
          </Box>
        )}

        {/* Pagination controls */}
        <TablePagination
          component='div'
          count={paginatedRowCount}
          page={pageIndex}
          onPageChange={(_evt, newPage) => table.setPageIndex(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(evt) => {
            const newSize = parseInt(evt.target.value, 10)
            table.setPageSize(newSize)
            table.setPageIndex(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showFirstButton
          showLastButton
        />
      </TableContainer>
    </Box>
  )
}
